import { ItemModel } from 'src/app/models/itemModel';
import type { LunaCreation } from 'src/app/models/luna-creation';
import type { AppIcon } from 'src/app/services/items-canvas.service';
import { WORKSPACE_COLUMN_WIDTH } from 'src/app/services/luna-workspace/luna-workspace-camera.util';

/** Board items that can become their own tab in Luna workspace mode (see isWorkspaceTabItem). */
export const WORKSPACE_APP_MODELS: ReadonlySet<ItemModel> = new Set([
  ItemModel.Mario,
  ItemModel.IFrame,
  ItemModel.WebViewer,
]);

/**
 * Whether an app item gets its own tab. Apps parked past the whiteboard column (Luna's shelf)
 * open full screen from the tab bar; an app placed inside the column stays on the whiteboard,
 * beside the work around it. An item with no known position keeps its tab.
 */
export function isWorkspaceTabItem(itemType: ItemModel, left: number | undefined): boolean {
  return isWorkspaceApp(itemType) && (left ?? WORKSPACE_COLUMN_WIDTH) >= WORKSPACE_COLUMN_WIDTH;
}

/** Whether an item is an app (on the shelf or inside the column). */
export function isWorkspaceApp(itemType: ItemModel): boolean {
  return WORKSPACE_APP_MODELS.has(itemType);
}

export const WHITEBOARD_TAB_ID = 'whiteboard';

export type WorkspaceTab =
  | { kind: 'whiteboard'; id: typeof WHITEBOARD_TAB_ID; label: string }
  | {
      kind: 'app';
      id: string;
      label: string;
      itemType: ItemModel;
      appKey: string | null;
      /** The app's own icon from the app catalog, when it has one. */
      icon: AppIcon | null;
    }
  | {
      /** An app Luna is still making: shown with its progress, and not openable yet. */
      kind: 'creating';
      id: string;
      label: string;
      creation: LunaCreation;
    };

export const WHITEBOARD_TAB: WorkspaceTab = {
  kind: 'whiteboard',
  id: WHITEBOARD_TAB_ID,
  label: 'Whiteboard',
};

/** The parts of an app item's configurations the tab bar reads. */
interface AppConfigurationsLike {
  title?: string;
  appInstanceId?: string;
  resource?: { _type?: string; name?: string; url?: string; title?: string };
}

const KNOWN_APP_KEYS = ['code-playground', 'tiptap', 'presentation'];

/**
 * CollaborativeApps.MARIO: the catalog entry an app is placed under when it has no record of its
 * own. Its title is the generic "Mario App", so it doesn't name a tab.
 */
const GENERIC_MARIO_APP_UID = 'MARIO';

const LABEL_BY_APP_KEY: Record<string, string> = {
  'code-playground': 'Code Editor',
  tiptap: 'Notes',
  presentation: 'Slides',
};

/** Which first-party Mario app this is (`code-playground`, `tiptap`, …), or null. */
export function workspaceAppKey(itemType: ItemModel, configurations: unknown): string | null {
  if (itemType !== ItemModel.Mario) {
    return null;
  }
  const config = (configurations ?? {}) as AppConfigurationsLike;
  const resource = config.resource;
  if (resource?._type === 'DEPLOYED' && resource.name) {
    return resource.name;
  }
  // The agent places some apps as a DEV_URL resource, so recognise them by their URL.
  const haystack = `${resource?.url ?? ''} ${config.appInstanceId ?? ''}`;
  return KNOWN_APP_KEYS.find((key) => haystack.includes(key)) ?? null;
}

/**
 * The app catalog's title (AppInformation, looked up by the caller) when the app has one, as the
 * board's app header shows it; otherwise a name from the app itself.
 */
export function workspaceTabLabel(
  itemType: ItemModel,
  configurations: unknown,
  catalogTitle?: string | null,
): string {
  const config = (configurations ?? {}) as AppConfigurationsLike;
  if (catalogTitle && config.appInstanceId !== GENERIC_MARIO_APP_UID) {
    return catalogTitle;
  }
  const appKey = workspaceAppKey(itemType, config);
  if (appKey && LABEL_BY_APP_KEY[appKey]) {
    return LABEL_BY_APP_KEY[appKey];
  }
  if (config.resource?._type === 'GENERATED' && config.resource.title) {
    return config.resource.title;
  }
  if (config.title) {
    return config.title;
  }
  return itemType === ItemModel.WebViewer ? 'Web viewer' : 'App';
}

/** How an app item is shown: on the board as today, hidden, full screen, or beside the whiteboard. */
export type AppPresentation = 'board' | 'hidden' | 'full' | 'split';

export interface WorkspaceViewState {
  enabled: boolean;
  fullscreenId: string | null;
  splitItemUid: string | null;
}

/**
 * `isTab` is false for an app placed inside the whiteboard column: it stays on the board, unless
 * the student opens it full screen (its own full-screen button).
 */
export function appPresentationFor(
  state: WorkspaceViewState,
  itemUid: string,
  isTab = true,
): AppPresentation {
  if (!state.enabled) {
    return 'board';
  }
  if (state.fullscreenId === itemUid) {
    return 'full';
  }
  if (!isTab) {
    return 'board';
  }
  if (state.splitItemUid === itemUid) {
    return 'split';
  }
  return 'hidden';
}
